import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLine, EASE } from "@/components/Reveal";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const goHowWeWork = (e) => {
    e.preventDefault();
    const el = document.querySelector("#how-we-work");
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -60 });
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative pt-40 sm:pt-52 pb-[220px] sm:pb-[280px] overflow-x-clip">
      <img
        src="/assets/blob.png"
        alt=""
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[110%] max-w-none opacity-70 pointer-events-none select-none"
      />
      <div className="wave-drift-mask absolute left-1/2 -translate-x-1/2 bottom-[-150px] w-[118vw] pointer-events-none select-none">
        <div className="wave-drift-track">
          <img src="/assets/waves.png" alt="" className="w-[118vw] max-w-none shrink-0" />
          <img src="/assets/waves.png" alt="" className="w-[118vw] max-w-none shrink-0" />
        </div>
      </div>

      <motion.div style={{ y: contentY }} className="relative site-container text-center mt">
        <h1
          data-testid="hero-heading"
          className="tracking-tight leading-[1.06] text-[30px] sm:text-[36px] md:text-[44px] lg:text-[54px] xl:text-[64px] 2xl:text-[74px]"
        >
          <MaskedLine delay={0.35} className="font-light">
            Creating Strategy, Content
          </MaskedLine>
          <MaskedLine delay={0.5} className="font-medium">
            <span className="inline-flex items-center gap-3 sm:gap-5 justify-center leading-none">
              <span className="leading-none">and Digital Solutions for Leaders</span>
              <span className="hidden font-light leading-none xl:inline">—</span>
              <motion.img
                src="/assets/globe.svg"
                alt=""
                className="hidden shrink-0 self-center xl:block"
                style={{ width: "1.2em", height: "1.2em", marginTop: "4px" }}
                initial={{ scale: 0, rotate: -90 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
              />
            </span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtext"
          className="mt-8 max-w-2xl mx-auto text-lg sm:text-[22px] leading-relaxed text-[#111]/60 font-normal"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
        >
          Own the narrative, earn the audience, and build a brand that gets found, remembered, and trusted everywhere it matters.
        </motion.p>

        <motion.div
          className="mt-10 flex items-center justify-center gap-4 flex-wrap"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
        >
          <Link to="/contact" data-testid="hero-book-call-btn" className="btn-pill btn-lav !border-[#8C5EBA] justify-center sm:w-64 text-[16px] !font-semibold">
            Book a Strategic Call
            <ArrowRight size={18} className="btn-arrow" />
          </Link>
          <a href="#how-we-work" onClick={goHowWeWork} data-testid="hero-how-it-works-btn" className="btn-pill btn-outline-accent justify-center sm:w-64 text-[16px] !font-semibold">
            How it works
            <ArrowRight size={18} className="btn-arrow" />
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
